import React, { useEffect, useMemo, useState } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { Image, Upload } from 'antd';
import type { UploadFile, UploadProps } from 'antd';
import ImgCrop from 'antd-img-crop';
import { imageUpload, videoUpload } from '@/services/swagger/imgstudio';
import { message as messageUtil } from '@/utils/antdInstance';
import { getBase64 } from '@/pages/GenerationMedia/utils/imgUtils';

type MediaType = 'image' | 'video';
type MediaAsset = {
  height?: number;
  http_url?: string;
  id?: string;
  name?: string;
  ratio?: string;
  thumbnail_http_url?: string;
  thumbnail_url?: string;
  type?: string;
  uid?: string;
  url?: string;
  width?: number;
  [key: string]: any;
};
type UploadMediaValue = MediaAsset[];

type UploadMediaProps = {
  accept: string[];
  children?: React.ReactNode;
  className?: string;
  /** 传入后启用 antd-img-crop 弹窗裁剪（固定比例 width/height；可缩放，不可旋转） */
  cropAspect?: number;
  defaultValue?: UploadMediaValue;
  disabled?: boolean;
  listType: UploadProps['listType'];
  maxCount: number;
  maxSizeMB: number;
  mediaType: MediaType;
  multiple?: boolean;
  onChange?: (value: UploadMediaValue) => void;
  onFileListChange?: (files: UploadFile[]) => void;
  onUploadStart?: () => void;
  onUploadSuccess?: (value: MediaAsset) => void;
  showUploadList: UploadProps['showUploadList'];
  uploadText?: React.ReactNode;
  value?: UploadMediaValue;
  format?: boolean;
  sourceSystem: string;
};

const getThumbnailUrl = (asset?: MediaAsset) => {
  if (!asset) return '';
  return asset.thumbnail_http_url || asset.http_url || asset.url || asset.thumbnail_url || '';
};

const getPreviewUrl = (asset?: MediaAsset) => {
  if (!asset) return '';
  return asset.http_url || asset.thumbnail_http_url || asset.url || asset.thumbnail_url || '';
};

const getMediaKey = (asset?: MediaAsset) => {
  if (!asset) return '';
  return asset.uid || asset.id || asset.url || asset.http_url || asset.thumbnail_http_url || asset.thumbnail_url || '';
};

const getMediaName = (asset: MediaAsset, index: number) => {
  const source = asset.name || asset.url || asset.http_url || asset.thumbnail_http_url || '';
  const fallback = `media-${index + 1}`;
  if (!source) return fallback;

  try {
    return decodeURIComponent(source.split('?')[0].split('/').pop() || fallback);
  } catch {
    return source.split('?')[0].split('/').pop() || fallback;
  }
};

const COMMON_IMAGE_MIME_TYPES = new Set(['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif']);

const isAcceptedImageFile = (file: File, acceptList: string[]) => {
  const mime = (file.type || '').toLowerCase();
  const fileName = (file.name || '').toLowerCase();
  const hasSpecificAccept = acceptList.some((item) => item !== 'image/*' && item !== 'video/*');

  if (!hasSpecificAccept) return mime.startsWith('image/');

  const acceptNormalized = acceptList.map((item) => item.toLowerCase());
  if (mime && (COMMON_IMAGE_MIME_TYPES.has(mime) || acceptNormalized.includes(mime))) return true;

  return acceptNormalized.some((item) => {
    if (item.startsWith('.')) return fileName.endsWith(item);
    if (item.includes('/')) return mime === item;
    return false;
  });
};

const getGreatestCommonDivisor = (a: number, b: number): number => {
  return b === 0 ? a : getGreatestCommonDivisor(b, a % b);
};

const getImageMeta = (file: File): Promise<Pick<MediaAsset, 'width' | 'height' | 'ratio'>> => {
  return new Promise((resolve) => {
    const image = document.createElement('img');
    const objectUrl = URL.createObjectURL(file);

    image.onload = () => {
      const width = image.naturalWidth;
      const height = image.naturalHeight;
      const gcd = getGreatestCommonDivisor(width, height);

      URL.revokeObjectURL(objectUrl);
      resolve({
        height,
        ratio: gcd ? `${width / gcd}:${height / gcd}` : undefined,
        width,
      });
    };

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve({});
    };

    image.src = objectUrl;
  });
};

const normalizeValueToFileList = (value?: UploadMediaValue, currentFileList: UploadFile[] = []): UploadFile[] => {
  const currentFileMap = new Map(currentFileList.map((file) => [getMediaKey(file.response as MediaAsset), file] as const).filter(([key]) => key));

  return (value || []).filter(Boolean).map((asset, index) => {
    const url = getThumbnailUrl(asset);
    const key = getMediaKey(asset);
    const currentFile = currentFileMap.get(key);

    return {
      ...currentFile,
      uid: currentFile?.uid || key || `media-${index}`,
      name: currentFile?.name || getMediaName(asset, index),
      status: 'done',
      url,
      thumbUrl: url,
      response: asset,
    };
  });
};

const normalizeUploadFile = (file: UploadFile): UploadFile => {
  const url = getThumbnailUrl(file.response as MediaAsset);
  if (!url) return file;

  return {
    ...file,
    url,
    thumbUrl: url,
  };
};

const getValueFromFileList = (files: UploadFile[]): UploadMediaValue =>
  files
    .filter((file) => file.status === 'done')
    .map((file) => file.response)
    .filter(Boolean) as UploadMediaValue;

const UploadMedia: React.FC<UploadMediaProps> = (props) => {
  const { accept, children, className, cropAspect, defaultValue, disabled, format, listType, maxCount, maxSizeMB, mediaType, multiple = false, onChange, onFileListChange, onUploadStart, onUploadSuccess, showUploadList, uploadText, value, sourceSystem = 'AI IMAGE' } = props;
  const [previewImage, setPreviewImage] = useState<string>('');
  const [previewOpen, setPreviewOpen] = useState<boolean>(false);
  const [fileList, setFileList] = useState<UploadFile[]>(() => normalizeValueToFileList(value || defaultValue));
  const hasUploadingFile = fileList.some((file) => file.status === 'uploading');
  const uploadDisabled = disabled || (!multiple && maxCount > 1 && hasUploadingFile);
  const enableImgCrop = typeof cropAspect === 'number' && mediaType === 'image';

  useEffect(() => {
    setFileList((currentFileList) => {
      const nextValue = value || defaultValue;
      const uploadingFiles = currentFileList.filter((file) => file.status === 'uploading');
      if (uploadingFiles.length > 0) {
        if (!nextValue?.length) return currentFileList;
        const normalizedFiles = normalizeValueToFileList(nextValue, currentFileList);
        const normalizedKeys = new Set(normalizedFiles.map((file) => getMediaKey(file.response as MediaAsset)).filter(Boolean));
        return [...normalizedFiles, ...uploadingFiles.filter((file) => !normalizedKeys.has(getMediaKey(file.response as MediaAsset)))];
      }
      return normalizeValueToFileList(nextValue, currentFileList);
    });
  }, [defaultValue, multiple, value]);

  const beforeImgUpload: UploadProps['beforeUpload'] = (file) => {
    if (!multiple && maxCount > 1 && hasUploadingFile) {
      messageUtil.warning('请等待当前素材上传完成');
      return Upload.LIST_IGNORE;
    }
    if (mediaType === 'image') {
      if (!isAcceptedImageFile(file as File, accept)) {
        messageUtil.error('请上传 jpg/jpeg/png/webp/gif 格式图片');
        return Upload.LIST_IGNORE;
      }
    } else if (mediaType === 'video') {
      if (!file.type.startsWith('video/')) {
        messageUtil.error('请上传 video 格式文件');
        return Upload.LIST_IGNORE;
      }
    }
    if (file.size > maxSizeMB * 1024 * 1024) {
      messageUtil.error(`${mediaType === 'image' ? '图片' : '视频'}大小不能超过 ${maxSizeMB}MB`);
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  const customRequest = async ({ file, onError, onSuccess }: any) => {
    try {
      const res = mediaType === 'image' ? await imageUpload({ file, source_system: sourceSystem }) : await videoUpload({ file, source_system: sourceSystem });
      if (res.code === 200 && res.data) {
        const imageMeta = format && mediaType === 'image' ? await getImageMeta(file) : {};
        const mediaAsset = format ? { ...res.data, format: file.type, ...imageMeta } : res.data;
        onUploadSuccess?.(mediaAsset);
        onSuccess?.(mediaAsset);
      } else {
        onError?.(new Error(res.msg || `${mediaType} upload failed`));
        messageUtil.error(res.msg || `${mediaType} 上传失败`);
      }
    } catch (error) {
      onError?.(error);
      console.error(`${mediaType} 上传失败:`, error);
    }
  };

  const handleImageChange: UploadProps['onChange'] = async ({ file, fileList: newFileList }: any) => {
    const nextFileList = newFileList.map(normalizeUploadFile);
    setFileList(nextFileList);
    onFileListChange?.(nextFileList);
    if (file.status === 'uploading') {
      onUploadStart?.();
      return;
    }
    onChange?.(getValueFromFileList(nextFileList));
  };

  const handlePreview = async (file: any) => {
    const responseUrl = getPreviewUrl(file.response);
    const fileUrl = responseUrl || file.url || file.thumbUrl || file.preview;

    if (!fileUrl && file.originFileObj) {
      getBase64(file.originFileObj as any, (url: string) => {
        file.preview = url;
        setPreviewImage(url);
        setPreviewOpen(true);
      });
      return;
    }
    setPreviewImage(fileUrl);
    setPreviewOpen(true);
  };

  const uploadButton = useMemo(() => {
    if (children) {
      return children;
    }

    return (
      <div>
        {uploadText ? (
          uploadText
        ) : (
          <>
            <PlusOutlined />
            <div style={{ marginTop: 8 }}>{mediaType === 'image' ? '上传图片' : '上传视频'}</div>
          </>
        )}
      </div>
    );
  }, [children, mediaType, uploadText]);

  const uploadNode = (
    <Upload accept={accept.join(',')} beforeUpload={beforeImgUpload} className={className} customRequest={customRequest} disabled={uploadDisabled} fileList={fileList} isImageUrl={() => mediaType === 'image'} listType={listType} maxCount={maxCount} multiple={multiple} onChange={handleImageChange} onPreview={handlePreview} showUploadList={showUploadList}>
      {fileList.length >= maxCount ? null : uploadButton}
    </Upload>
  );

  return (
    <>
      {enableImgCrop ? (
        <ImgCrop aspect={cropAspect} aspectSlider={false} modalCancel="取消" modalOk="确定" modalTitle="编辑图片" quality={0.9} rotationSlider={false} zoomSlider>
          {uploadNode}
        </ImgCrop>
      ) : (
        uploadNode
      )}
      {previewImage && (
        <Image
          preview={{
            visible: previewOpen,
            onVisibleChange: (visible) => setPreviewOpen(visible),
            afterOpenChange: (visible) => !visible && setPreviewImage(''),
          }}
          src={previewImage}
          wrapperStyle={{ display: 'none' }}
        />
      )}
    </>
  );
};

export default UploadMedia;
